/**
 * fctLabel — resolve the source-ability label shown under floating combat
 * text numbers (DJ 2026-06-12: the capture review's "30 then 30+22"
 * fireball frames were unattributable — which hit was the impact and which
 * the burn tick?).
 *
 * Gated by UserPrefs ui.fctAbilityNames (Settings → Interface, default on).
 * The pref is cached module-level and kept fresh via subscribeUserPrefs —
 * FCT fires per hit, so no per-call localStorage parse.
 */
import { storage } from '../../services/StorageService';
import { getAbilityData } from '../../../shared/content/generated/abilities.gen';

let enabled: boolean | null = null;

function isEnabled(): boolean {
  if (enabled === null) {
    try {
      enabled = storage.loadUserPrefs().ui.fctAbilityNames;
      storage.subscribeUserPrefs((p) => { enabled = p.ui.fctAbilityNames; });
    } catch {
      enabled = true; // storage unavailable (tests/SSR) — labels stay useful
    }
  }
  return enabled;
}

/**
 * Label for a damage number's source, or null when disabled/unknown.
 * `tick: true` marks DoT/periodic ticks — "Burn (tick)".
 *
 * A DoT strike's id is `status:<effectId>` (CombatStrike), which no ability
 * matches; title-casing it printed "Status:Poison" under the number, beside
 * the tick's own "Poison (tick)" beat and the target's pip (plan 15 §2.4).
 * Those already name the status, so `status:` sources get no label.
 */
export function fctAbilityLabel(
  abilityId: string | null | undefined,
  opts?: { tick?: boolean },
): string | null {
  if (!abilityId || abilityId.startsWith('status:') || !isEnabled()) return null;
  const name = abilityId.startsWith('mob:') ? mobStrikeName(abilityId) : abilityDisplayName(abilityId);
  if (!name) return null;
  return opts?.tick ? `${name} (tick)` : name;
}

/**
 * The player-facing name of an ability id — the content name, or the id
 * title-cased when content has none. Players never see a raw `basic_strike`.
 */
export function abilityDisplayName(abilityId: string): string {
  return getAbilityData(abilityId)?.name
    ?? abilityId.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
}

/**
 * A mob template strike is `mob:<type>` (its plain swing) or
 * `mob:<type>:<move>` (a pig's bump, a boar's charge). Title-casing the whole
 * id printed "Mob:Pig:Bump" under the number; the move reads "Bump", and the
 * plain swing needs no label (the number already sits on the mob's victim).
 */
function mobStrikeName(abilityId: string): string | null {
  const parts = abilityId.split(':');
  return parts.length >= 3 ? abilityDisplayName(parts[parts.length - 1]) : null;
}

/** Test seam — drop the cached pref so specs can re-prime it. */
export function __resetFctLabelForTests(): void {
  enabled = null;
}
